<template>
  <b-container fluid class="mb-4">
    <b-row>
      <Filters />
      <b-col class="search-section">
        <SearchHeader />
        <SlicesBlock
          v-if="existContent('topContent')"
          :slices="topContent(getActualDimension.path)"
        />
        <SponsorBanner
          v-if="!isPaidPlan"
          :query="$route.params.search || query"
          :page="currentPage"
          :origin="$route.name"
        />
        <SearchWildcard />
        <WithoutResult />
        <GridCSS :items="searchResult" :send-vector-click="true">
          <template #default="slotProps">
            <NormalVectorOverlay :data="slotProps.data" />
          </template>
        </GridCSS>
        <BlackFridayBanner />
        <SearchPaginator class="mt-3" />
        <SlicesBlock
          v-if="existContent('bottomContent')"
          :slices="bottomContent(getActualDimension.path)"
        />
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { uids } from '~/customHtmls.json';
import { getOGImage } from '~/utils/common.js';

import { mapState, mapGetters } from 'vuex';

import Filters from '@/components/SearchResults/Filters';
import SearchHeader from '@/components/SearchResults/SearchHeader';
import SearchWildcard from '@/components/SearchResults/SearchWildcard';
import WithoutResult from '@/components/SearchResults/WithoutResult';
import GridCSS from '@/components/Common/GridCSS';
import NormalVectorOverlay from '@/components/Vectors/NormalVectorOverlay';
import SearchPaginator from '@/components/SearchResults/SearchPaginator';
import SlicesBlock from '@/components/Prismic/CustomHtml/SlicesBlock.vue';
import BlackFridayBanner from '@/components/SearchResults/BlackFridayBanner.vue';
import SponsorBanner from '@/components/SponsorBanner';

export default {
  components: {
    SearchPaginator,
    SearchWildcard,
    SearchHeader,
    Filters,
    GridCSS,
    NormalVectorOverlay,
    WithoutResult,
    SlicesBlock,
    BlackFridayBanner,
    SponsorBanner
  },
  data() {
    return {
      seoFields: { canonical: {} },
      query: ''
    };
  },
  async fetch() {
    const locale = this.$store.$i18n.locale;
    const search = this.$route.params.search;
    const regexPages = /((-p)$|(-p-page))/;

    let slugName = this.getRouteBaseName().replace(regexPages, '');

    if (slugName === 'merch-vectors') {
      this.query = 'cat';
    } else if (slugName === 'merch-png') {
      this.query = 'Vintage';
    } else if (slugName === 'merch-designs') {
      this.query = 'Cute';
    }

    const slugs = {
      en: this.localePath({ name: slugName, params: { slug: search } }, 'en'),
      de: this.localePath({ name: slugName, params: { slug: search } }, 'de'),
      es: this.localePath({ name: slugName, params: { slug: search } }, 'es'),
      pt: this.localePath({ name: slugName, params: { slug: search } }, 'pt')
    };

    const routeBaseName = this.getRouteBaseName();
    const regexDesignPages = /(graphics|svg-png)/;
    const slugDesignName = routeBaseName.match(regexDesignPages);

    if (slugDesignName?.length) {
      this.seoFields.canonical = this.getDimensionCanonical(routeBaseName, locale, search);
    } else {
      this.seoFields.canonical = this.$canonicalPlugin(slugs[`${locale}`], locale, true);
    }

    const paramsPage = this.$route.params.page;
    let currentSearch = search ? search.replace(/-/g, ' ') : '';
    const matchesSearchP = this.getRouteBaseName().match(/((-p)$)/);
    if (matchesSearchP && !paramsPage) {
      currentSearch = 'p';
    }

    let promises = [];
    if (this.getSilentSearch) {
      this.$store.commit('search/SET_SILENT_SEARCH', false);
    } else {
      const currentPage = paramsPage ? parseInt(paramsPage) : 1;
      const searchPromise = this.$store.dispatch('search/MAKE_SEARCH', {
        criteria: currentSearch,
        page: currentPage
      });
      promises = promises.concat([searchPromise]);
    }

    const dimensionUid = `dimension-${this.getActualDimension.path}`;
    const hasCustomHtml = search
      ? uids.searches.find((el) => el[locale] === search)
      : uids.dimensions.find((el) => el[locale] === dimensionUid);

    if (hasCustomHtml) {
      const customHtmlPromise = this.$store.dispatch('customHtml/LOAD_CUSTOM_HTML', {
        uid: search || dimensionUid,
        dimension: this.getActualDimension.path
      });
      promises.push(customHtmlPromise);
    } else {
      this.$store.commit('customHtml/SET_ACTUAL_CONTENT', {});
      this.$store.commit('customHtml/SET_CUSTOM_HTML', {});
    }

    return Promise.all(promises);
  },
  head() {
    const i18nSeo = this.$seoPlugin({
      hreflang: false,
      canonical: false,
      slash: true
    });
    const openGraph = this.$openGraphPlugin(
      this.metadata?.metaTitle,
      'website',
      this.$route.path,
      this.metadata?.metaDescription,
      this.getOpenGraphImage,
      this.$store.state.i18n.locale
    );
    return {
      title: this.metadata?.metaTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.metadata?.metaDescription
        },
        {
          hid: 'robots',
          name: 'robots',
          content: this.hasLittleContent || this.isPaged ? 'noindex, follow' : 'all'
        },
        ...openGraph
      ],
      link: [this.seoFields.canonical, ...i18nSeo.link],
      script: [
        {
          type: 'application/ld+json',
          json: this.jsonLd
        }
      ]
    };
  },
  computed: {
    ...mapState('search', ['searchResult', 'wildcard', 'metadata', 'currentPage', 'totalPages']),
    ...mapGetters('search', ['getImageFirstResult', 'getSilentSearch']),
    ...mapState('searchBar', ['actualSearch']),
    ...mapGetters('dimensions', ['getDimensionsByExperience', 'getActualDimension']),
    ...mapGetters('customHtml', ['existContent', 'topContent', 'bottomContent']),
    hasLittleContent() {
      return this.searchResult.length == 0;
    },
    isPaged() {
      return this.currentPage > 1;
    },
    getOpenGraphImage() {
      return this.$route.params.search ? this.getImageFirstResult : getOGImage(this.$expKey());
    },
    isPaidPlan() {
      return (
        this.$auth.user && this.$auth.user.idPlan !== 1 && !this.$auth.user.isFreeTrialSubscriptor
      );
    },
    jsonLd() {
      const domain = this.getCurrentDomainByLanguage(this.$i18n.locale);
      return this.searchResult.map((content) => {
        return {
          '@context': 'https://schema.org',
          '@type': 'ImageObject',
          contentUrl: content.image,
          name: content.name,
          thumbnailUrl: content.image + '?w=360',
          creator: {
            '@type': 'Organization',
            name: 'Vexels'
          },
          acquireLicensePage: `${domain}/merch-plans/`,
          copyrightNotice: 'Vexels',
          license: 'https://www.vexels.com/terms-and-conditions/',
          creditText: 'Vexels',
          identifier: content.id
        };
      });
    },
    currentLanguage() {
      let currentLang = this.$i18n.locale.toUpperCase();
      return currentLang === 'PT' ? 'BR' : currentLang;
    }
  },
  beforeDestroy() {
    if (!this.getSilentSearch) {
      this.$store.dispatch('search/CLEAN_SEARCH');
    }
  },
  methods: {
    getDimensionCanonical(route, lang, search) {
      let canonical;
      let url;
      if (route.includes('graphics')) {
        if (search) {
          url = this.localePath({ name: 'merch-designs-search', params: { slug: search } }, lang);
        } else {
          url = this.localePath({ name: 'merch-designs', params: { slug: search } }, lang);
        }
      } else if (route.includes('svg-png')) {
        if (search) {
          url = this.localePath({ name: 'merch-png-search', params: { slug: search } }, lang);
        } else {
          url = this.localePath({ name: 'merch-png', params: { slug: search } }, lang);
        }
      }
      canonical = this.$canonicalPlugin(url, this.$store.$i18n.locale, true);

      return canonical;
    },
    getCurrentDomainByLanguage(code) {
      let domain = this.$i18n.locales.find((item) => item.code == code).domain;
      return `https://${domain}`;
    }
  }
};
</script>
